import { useContext, useEffect } from "react";
import { createUseStyles } from "react-jss";
import PropTypes from "prop-types";
import _ from "lodash";
import jssVariables from "../../jss_setup";
import { Form, Formik } from "formik";
import { TranslationContext, countries } from "../../umbilical_tools";
import {
  InputField,
  TextAreaField,
  commonInputGroupStyles,
} from "../../components/forms/inputGroup";
import { Button } from "../../components/common/Button";
import { fieldGroups, getInitialValues, PasswordRequirements, validationSchema } from "./helpers";
import { SelectField } from "../../components/forms/inputGroup/helpers";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Icon } from "@iconify/react";
import { modalGenericStyles } from "../../jss_setup/modals";
import { useMutation } from "@tanstack/react-query";
import { Loader } from "rsuite";
import CheckBoxComp from "../../components/forms/inputGroupNoFormik/CheckBox";
import { registerSubagent, updateSubagentDocument, uploadSubagentDocument } from "../../api";

const registrationFormStyles = createUseStyles({
  RegistrationForm: (props) => ({
    position: "relative",
    height: "100%",
    ...(props?.customStyles?.RegistrationForm ?? {}),
  }),
  section: (props) => ({
    display: "grid",
    gridGap: jssVariables.normal_gap,
    ...(props?.customStyles?.section ?? {}),
  }),
  sectionTitle: (props) => ({
    fontSize: "x-large",
    fontWeight: 500,
    color: jssVariables.colors.base_color,
    ...(props?.customStyles?.sectionTitle ?? {}),
  }),
  form: (props) => ({
    display: "grid",
    gridGap: `calc(${jssVariables.normal_gap} * 3)`,
    padding: [jssVariables.normal_gap, 0],
    ...(props?.customStyles?.form ?? {}),
  }),
  sectionSpan2: (props) => ({
    ...(props?.customStyles?.sectionSpan2 ?? {}),
  }),
  sectionSpan3: (props) => ({
    ...(props?.customStyles?.sectionSpan3 ?? {}),
  }),
  btnContr: (props) => ({
    ...(props?.customStyles?.btnContr ?? {}),
  }),
  label: { ...commonInputGroupStyles.label },
  loadingCntr: {
    position: "absolute",
    top: 0,
    left: 0,
    display: "grid",
    gridAutoRows: "max-content",
    gridGap: jssVariables.double_gap,
    alignContent: "center",
    justifyItems: "center",
    height: "100%",
    width: "100%",
    zIndex: 1,
    background: "white",
    borderRadius: jssVariables.normal_gap,
  },
  loadingIcon: { transform: "scale(2.5)" },
  loadingTxt: { fontSize: "x-large", fontWeight: 500 },
  fileCntr: {
    display: "grid",
    gridGap: jssVariables.half_gap,
    justifyContent: "start",
  },
  uploadBtn: {
    padding: jssVariables.half_gap,
    background: jssVariables.colors.base_color,
    color: "white",
    fontWeight: 500,
    width: "10rem",
    borderRadius: jssVariables.half_gap,
  },
  error: {
    // paddingTop: jssVariables.half_gap,
    color: "red",
    wordWrap: "break-word",
    textTransform: "capitalize",
  },
  [`@media ${jssVariables.media.smallscreen}`]: {
    form: (props) => ({
      gridTemplateColumns: "repeat(3, 1fr)",
      gridGap: `${jssVariables.double_gap} calc(${jssVariables.normal_gap} * 4)`,
      padding: 0,
      ...(props?.customStyles?.form?.smallscreen ?? {}),
    }),
    section: (props) => ({
      gridGap: `calc(${jssVariables.normal_gap} / 3) calc(${jssVariables.normal_gap} * 4)`,
      alignContent: "baseline",
      ...(props?.customStyles?.section?.smallscreen ?? {}),
    }),
    btnContr: (props) => ({
      gridColumn: "1 / span 3",
      ...(props?.customStyles?.btnContr?.smallscreen ?? {}),
    }),
    sectionTitle: (props) => ({
      fontSize: "large",
      ...(props?.customStyles?.sectionTitle?.smallscreen ?? {}),
    }),
    sectionSpan2: (props) => ({
      gridColumn: "1 / span 2",
      ...(props?.customStyles?.sectionSpan2?.smallscreen ?? {}),
    }),
    sectionSpan3: (props) => ({
      gridColumn: "1 / span 3",
      ...(props?.customStyles?.sectionSpan3?.smallscreen ?? {}),
    }),
    error: (props) => ({
      maxWidth: "10rem",
      ...props?.customStyles?.error?.smallscreen,
    }),
  },
});
export function RegistrationForm({ setShowModal }: { setShowModal: (show: boolean) => void }) {
  const isDocRequired = true;

  const classes = registrationFormStyles({});

  const { t } = useContext(TranslationContext) as { t: (key: string) => string };

  const { source_entity } = useSelector(
    (state: { WebsiteMetadata: { metadata: { source_entity: string } } }) => {
      const source_entity = state.WebsiteMetadata.metadata.source_entity ?? "";
      return { source_entity };
    },
  );

  const { mutate: documentMutation, isLoading: docLoading } = useMutation({
    mutationFn: ({ formData, payload }) => uploadSubagentDocument(formData),
    onSuccess: (data, values) => {
      const uid = data?.data?.uid;
      registrationMutation({ payload: values.payload, fileUid: uid });
    },
    onError: () => {
      toast.error("Failed to upload document.", { autoClose: 3000 });
    },
  });

  const { mutate: documentUpdateMutation, isLoading: docUpdateLoading } = useMutation({
    mutationFn: ({ uid, source_entity }: { uid: string; source_entity: string }) => {
      return updateSubagentDocument(uid, source_entity);
    },
    onSuccess: () => {
      setShowModal(true);
    },
    onError: () => {
      toast.error("Failed to upload document.", { autoClose: 3000 });
    },
  });

  const { mutate: registrationMutation, isLoading: dataLoading } = useMutation({
    mutationFn: ({ payload, fileUid }) => registerSubagent({ payload }),
    onSuccess: (data, values) => {
      toast.success("Registration successful.", { autoClose: 3000 });
      const source_entity = `${data?.data?.id}___subagent`;
      documentUpdateMutation({ uid: values.fileUid, source_entity });
    },
    onError: (error) => {
      Object.entries(error?.response?.data ?? {})?.forEach(([field, messages]) => {
        toast.error(
          <p>
            <span>
              <strong>{_.capitalize(field)}</strong>
            </span>
            <ul>
              {messages.map((msg, idx) => (
                <li key={idx}>{msg}</li>
              ))}
            </ul>
          </p>,
          { autoClose: 10000 },
        );
      });
    },
  });

  function renderError(errors) {
    return errors ? (
      <div className={classes.error}>
        <small>{errors}</small>
      </div>
    ) : (
      <div className={classes.error} />
    );
  }

  return (
    <div className={classes.RegistrationForm}>
      {(dataLoading || docLoading || docUpdateLoading) && (
        <div className={classes.loadingCntr}>
          <Loader className={classes.loadingIcon} />
          <div className={classes.loadingTxt}>Loading...</div>
        </div>
      )}
      <Formik
        initialValues={getInitialValues()}
        validateOnChange={false}
        validateOnBlur={false}
        validationSchema={validationSchema(t, isDocRequired)}
        onSubmit={async (values) => {
          const member_id = Number(source_entity.split("___")[0]);
          const certificate_of_incorporation = values.certificate_of_incorporation;
          const formData = new FormData();
          formData.append("file", certificate_of_incorporation);
          formData.append("tags", ["subagent_documents"]);
          formData.append("description", "Subagent Documents");
          documentMutation({ formData, payload: { ...values, member_id } });
        }}
      >
        {({ values, errors, setFieldValue }) => {
          const cert = values?.certificate_of_incorporation;
          const supportedFileTypes = ".pdf,.docx,.doc,.ppt,.pptx,.rtf";
          return (
            <Form className={classes.form}>
              {fieldGroups.map((fieldGroup, idx) => (
                <div
                  key={`${idx}-${fieldGroup.title}`}
                  className={`${classes.section} ${idx === 0 ? classes.sectionSpan3 : ""}`}
                >
                  <div
                    className={`${classes.sectionTitle} ${idx === 0 ? classes.sectionSpan3 : ""}`}
                  >
                    {" "}
                    {t(fieldGroup.title)}
                  </div>
                  {fieldGroup.items.map((item, index: number) => {
                    return item.name === "about_us" ? (
                      <div
                        className={classes.inputContainer}
                        key={`fieldGroup-${item.name}-${index}`}
                      >
                        <TextAreaField
                          label={`${t(item.label)}${item.required ? "*" : ""}`}
                          name={item.name}
                          id="about_us"
                          customStyles={{
                            label: { fontWeight: 500 },
                            requiredLabel: { fontSize: "small" },
                            input: { resize: "none" },
                          }}
                        />
                        {renderError(errors?.about_us)}
                      </div>
                    ) : item.name === "country_code" ? (
                      <div key={`fieldGroup-${item.name}-${index}`}>
                        <SelectField
                          label={`${t("country")}${item.required ? "*" : ""}`}
                          name="country_code"
                          id="country_code"
                          options={Object.entries(countries).map(([key, value]) => [key, value])}
                          customStyles={{
                            label: { fontWeight: 500 },
                            input: { height: "2.2rem!important" },
                            requiredLabel: { fontSize: "small" },
                          }}
                          placeholder="----"
                        />
                        {renderError(errors?.country_code)}
                      </div>
                    ) : item?.type === "file" ? (
                      <div key={`fieldGroup-${item.name}-${index}`} className={classes.fileCntr}>
                        <label htmlFor="file" className={classes.label}>
                          {item.label}
                        </label>
                        <input
                          style={{ display: "none" }}
                          onChange={(e) => setFieldValue(item.name, e.target.files?.[0])}
                          id="file"
                          name={item.name}
                          type="file"
                          accept={supportedFileTypes}
                        />
                        {renderError(errors?.certificate_of_incorporation)}
                        <button
                          type="button"
                          className={classes.uploadBtn}
                          onClick={(e) => {
                            e.preventDefault();
                            document.getElementById("file").click();
                          }}
                        >
                          Upload File
                        </button>
                        <small>
                          <span>
                            {" "}
                            Type: {cert?.type ? _.capitalize(cert.type.split("/")[1]) : "N/A"},
                          </span>{" "}
                          <span>
                            {" "}
                            Size: {((cert?.size ?? 0) / 1000).toFixed()} KB,{" "}
                            <span
                              style={{
                                color: ((cert?.size ?? 0) / 1000).toFixed() > 2000 && "red",
                              }}
                            >
                              Max : 2000KB
                            </span>
                          </span>
                        </small>
                        <small>Supported file types: {supportedFileTypes}</small>
                      </div>
                    ) : item.type === "checkbox" ? (
                      <div className="col2max" key={`fieldGroup-${item.name}-${index}`}>
                        <CheckBoxComp
                          checked={values.terms_and_conditions}
                          setChecked={(c) => setFieldValue("terms_and_conditions", c)}
                        />
                        <label htmlFor="terms_and_conditions">
                          <a
                            rel="noreferrer"
                            target="_blank"
                            href="/terms-and-conditions"
                            style={{ cursor: "pointer" }}
                          >
                            Accept Terms & Conditions
                          </a>
                          {renderError(errors?.terms_and_conditions)}
                        </label>
                      </div>
                    ) : (
                      <div
                        className={classes.inputContainer}
                        key={`fieldGroup-${item.name}-${index}`}
                      >
                        <InputField
                          as="input"
                          type={item.name.includes("password") ? "password" : "text"}
                          label={`${t(item.label)}${item.required ? "*" : ""}`}
                          name={item.name}
                          id={item.name}
                          placeholder={item.name === "url" ? "http://www.example.com" : "----"}
                          customStyles={{
                            label: { fontWeight: 500 },
                            input: { height: "2.2rem" },
                            requiredLabel: { fontSize: "small" },
                          }}
                        />
                        {item.name === "password" && <PasswordRequirements />}
                        {!!errors?.[item.name] && renderError(errors?.[item.name])}
                      </div>
                    );
                  })}
                </div>
              ))}
              <div className={classes.btnContr}>
                <Button id="submitBtn" type="submit" text={t("register")} size="large" />
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
}
RegistrationForm.propTypes = { setShowModal: PropTypes.func.isRequired };

const successModalStyles = createUseStyles({
  ...modalGenericStyles,

  SuccessModal: (props) => ({
    ...modalGenericStyles.modal,
    ...(props?.customStyles?.SuccessModal ?? {}),
  }),
  successModalCard: (props) => ({
    ...modalGenericStyles.card,
    display: "grid",
    gridGap: jssVariables.normal_gap,
    padding: `${jssVariables.navbar_height} calc(${jssVariables.navbar_height} * 2)`,
    placeItems: "center",
    borderRadius: jssVariables.border_radius,
    ...(props?.customStyles?.successModalCard ?? {}),
  }),
  successIcon: (props) => ({
    color: "#5dcb5e",
    fontSize: "xxx-large",
    ...(props?.customStyles?.successIcon ?? {}),
  }),
  successModalText: (props) => ({
    fontSize: "x-large",
    textAlign: "center",
    ...(props?.customStyles?.successModalText ?? {}),
  }),
  loadingIcon: (props) => ({
    color: jssVariables.colors.base_color,
    fontSize: "xx-large",
    ...(props?.customStyles?.loadingIcon ?? {}),
  }),
});
export function SuccessModal() {
  const classes = successModalStyles({});

  const { t } = useContext(TranslationContext) as { t: (key: string) => string };
  function redirectWithDelay() {
    setTimeout(() => {
      window.location.href = "/";
    }, 5000);
  }

  useEffect(() => {
    redirectWithDelay();
  }, []);

  return (
    <div className={classes.SuccessModal}>
      <div className={classes.successModalCard}>
        <div className={classes.successModalText}>
          <Icon icon="ep:success-filled" className={classes.successIcon} />
          <h3> {t("registrationSuccess")}</h3>
          <br />
          <p>{t("pleaseWaitFewMomentsAndYouWillBeRedirectedToHome")}</p>
        </div>
        <Icon icon="eos-icons:loading" className={classes.loadingIcon} />
      </div>
    </div>
  );
}
